import { io } from 'socket.io-client';

// const DEFAULT_SOCKET_URL = 'https://localhost:3001';
const DEFAULT_SOCKET_URL = 'http://localhost:3001';

const getSocketUrl = () => {
  const reactAppUrl =
    typeof process !== 'undefined' && process.env
      ? process.env.REACT_APP_SOCKET_URL
      : undefined;

  const viteUrl =
    typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env.VITE_SOCKET_URL
      : undefined;

  const envUrl = reactAppUrl || viteUrl;
  if (envUrl) return envUrl.trim().replace(/\/$/, '');

// Fallback : use same host as the frontend, port 3001
       if (typeof window !== 'undefined' && window.location) {
           return window.location.origin;
       }
       

  return DEFAULT_SOCKET_URL.trim().replace(/\/$/, '');
};

const SOCKET_URL = getSocketUrl();

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnecting = false;
    this.connectionTimeout = null;
    this.hasConnectionFailed = false;
    this.hasTriedFallback = false;
  }

  connect() {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.socket?.active) {
      return this.socket;
    }

    if (this.socket) {
      this.socket.connect();
      return this.socket;
    }

    this.isConnecting = true;
    this.hasConnectionFailed = false;

    // Clear any existing timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
    }

    // Set connection timeout (10 seconds)
    this.connectionTimeout = setTimeout(() => {
      if (!this.socket?.connected) {
        this.hasConnectionFailed = true;
        console.error('❌ Socket connection timeout after 10s');
        const errorEvent = new CustomEvent('socket:connection-timeout', { detail: { url: SOCKET_URL } });
        window.dispatchEvent(errorEvent);
      }
    }, 10000);

    try {
      const token = sessionStorage.getItem('token');
      console.log('Socket connecting to:', SOCKET_URL, 'with token:', token ? 'present' : 'absent');

      this.socket = io(SOCKET_URL, {
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
        auth: token ? { token } : {},
        autoConnect: true,
        timeout: 15000,
        upgrade: true,
        withCredentials: true,
      });

      this.socket.on('connect', () => {
        console.log('✅ Socket connected:', this.socket.id);
        this.isConnecting = false;
        this.hasConnectionFailed = false;
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message, 'URL:', SOCKET_URL);
        this.isConnecting = false;
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        this.hasConnectionFailed = true;
        const errorEvent = new CustomEvent('socket:connection-error', { detail: { error: error.message, url: SOCKET_URL } });
        window.dispatchEvent(errorEvent);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
        this.isConnecting = false;
        if (reason === 'io client disconnect') {
          this.isManualDisconnect = false;
        }
      });

      this.socket.on('reconnect_attempt', (attempt) => {
        console.log(`Reconnection attempt ${attempt}/5 to ${SOCKET_URL}`);
        const token = sessionStorage.getItem('token');
        if (token) {
          this.socket.auth = { token };
        }
      });

      this.socket.on('unauthorized', (error) => {
        console.error('Socket unauthorized:', error);
      });

      return this.socket;
    } catch (error) {
      this.isConnecting = false;
      this.hasConnectionFailed = true;
      throw error;
    }
  }

  isConnectionFailed() {
    return this.hasConnectionFailed;
  }

  getConnectionUrl() {
    return SOCKET_URL;
  }

  disconnect() {
    console.log('Manual socket disconnect');
    this.isManualDisconnect = true;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.hasTriedFallback = false;
  }

  getSocket() {
    return this.socket;
  }

  getSocketId() {
    return this.socket?.id;
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  // --- Room operations ---

  createRoom(name, userName) {
    return new Promise((resolve, reject) => {
      this.socket.emit('create-room', { name, userName }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  joinRoom(roomId, userName) {
    return new Promise((resolve, reject) => {
      this.socket.emit('join-room', { roomId, userName }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  startRoomCall(roomId, userName) {
    return new Promise((resolve, reject) => {
      this.socket.emit('start-room-call', { roomId, userName }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  leaveRoom(roomId) {
    return new Promise((resolve, reject) => {
      this.socket.emit('leave-room', { roomId }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  getRoomInfo(roomId) {
    return new Promise((resolve, reject) => {
      this.socket.emit('get-room-info', { roomId }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  getRooms() {
    return new Promise((resolve, reject) => {
      this.socket.emit('get-rooms', (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  // --- Signaling operations ---

  sendOffer(targetId, offer) {
    this.socket.emit('offer', { targetId, offer });
  }

  sendAnswer(targetId, answer) {
    this.socket.emit('answer', { targetId, answer });
  }

  sendIceCandidate(targetId, candidate) {
    this.socket.emit('ice-candidate', { targetId, candidate });
  }

  requestPeerConnections() {
    this.socket.emit('request-peer-connections');
  }

  // --- Media operations ---

  toggleAudio(isMuted) {
    this.socket.emit('toggle-audio', { isMuted });
  }

  toggleVideo(isVideoOff) {
    this.socket.emit('toggle-video', { isVideoOff });
  }

  startScreenShare() {
    this.socket.emit('start-screen-share');
  }

  stopScreenShare() {
    this.socket.emit('stop-screen-share');
  }

  requestScreenShare() {
    this.socket.emit('request-screen-share');
  }

  approveScreenShare(participantId) {
    this.socket.emit('approve-screen-share', { participantId });
  }

  denyScreenShare(participantId) {
    this.socket.emit('deny-screen-share', { participantId });
  }

  // --- Host controls ---

  muteParticipant(participantId) {
    this.socket.emit('mute-participant', { participantId });
  }

  muteAllParticipants() {
    this.socket.emit('mute-all-participants');
  }

  kickParticipant(participantId) {
    this.socket.emit('kick-participant', { participantId });
  }

  endMeeting() {
    this.socket.emit('end-meeting');
  }

// --- Recording operations ---

startRecording(roomId) {
  this.socket.emit('start-recording', {
    roomId
  });
}

stopRecording(roomId) {
  this.socket.emit('stop-recording', {
    roomId
  });
}

// --- Chat operations ---

   sendMessage(message) {
     return new Promise((resolve, reject) => {
       this.socket.emit('send-message', { message }, (response) => {
         if (response.success) resolve(response);
         else reject(new Error(response.error));
       });
     });
   }

   editMessage(messageId, nouveauContenu) {
     return new Promise((resolve, reject) => {
       this.socket.emit('edit-message', { messageId, nouveauContenu }, (response) => {
         if (response.success) resolve(response);
         else reject(new Error(response.error));
       });
     });
   }

   deleteMessage(messageId) {
     return new Promise((resolve, reject) => {
       this.socket.emit('delete-message', { messageId }, (response) => {
         if (response.success) resolve(response);
         else reject(new Error(response.error));
       });
     });
   }

  sendTypingIndicator(isTyping) {
    this.socket.emit('typing', { isTyping });
  }

  // --- Waiting room operations ---

  joinWaitingRoom(roomId, userName) {
    return new Promise((resolve, reject) => {
      this.socket.emit('join-waiting-room', { roomId, userName }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  approveFromWaitingRoom(participantId) {
    return new Promise((resolve, reject) => {
      this.socket.emit('approve-from-waiting-room', { participantId }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  denyFromWaitingRoom(participantId) {
    return new Promise((resolve, reject) => {
      this.socket.emit('deny-from-waiting-room', { participantId }, (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  // --- Hand raise ---

  raiseHand() {
    return new Promise((resolve, reject) => {
      this.socket.emit('raise-hand', (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  lowerHand() {
    return new Promise((resolve, reject) => {
      this.socket.emit('lower-hand', (response) => {
        if (response.success) resolve(response);
        else reject(new Error(response.error));
      });
    });
  }

  // --- Event listeners ---

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
}

const socketService = new SocketService();
export default socketService;
